// Shapes the pipeline's compact artifacts (result.json, derived-income.json,
// income-field-decisions.json) into the Loan -> Borrower -> Employment ->
// Base/Bonus/Overtime/Commission tree the detail view renders. Pure + defensive:
// artifacts can be partial, so every access is guarded and either source can be
// missing.

export const INCOME_TYPES = ["Base", "Bonus", "Overtime", "Commission"];

const STATUS_LABELS = {
  qualifying: "qualifying",
  conditional: "conditional",
  excluded: "excluded",
};

/**
 * derived-income.json component fieldKeys look like
 * `borrower-1/employment-1/computed/base-monthly`; the field-decisions tree
 * keys employments as `borrower-1-employment-1`. Reconcile to the latter.
 */
function employmentIdFromFieldKey(fieldKey) {
  if (typeof fieldKey !== "string") {
    return null;
  }
  const parts = fieldKey.split("/");
  if (parts.length < 2) {
    return null;
  }
  const [borrowerId, employmentId] = parts;
  if (employmentId.startsWith(`${borrowerId}-employment-`)) {
    return employmentId;
  }

  return `${borrowerId}-${employmentId}`;
}

function normalizeStatus(status, fallback) {
  if (Object.values(STATUS_LABELS).includes(status)) {
    return status;
  }
  if (status === "manual-review") {
    return STATUS_LABELS.conditional;
  }

  return fallback;
}

function normalizeRule(rule) {
  return {
    ruleId: rule.ruleId,
    excerpt: rule.excerpt || null,
    sourceId: rule.sourceId || null,
    evidenceSatisfied:
      typeof rule.evidenceSatisfied === "boolean"
        ? rule.evidenceSatisfied
        : null,
    requiredEvidence: rule.requiredEvidence || {},
  };
}

function indexGuidelineApplications(formulaRules, applicationsKey) {
  const byId = {};
  const byEmploymentAndType = {};
  const applications =
    formulaRules?.[applicationsKey] ||
    (applicationsKey === "reconciliationGuidelineApplications"
      ? formulaRules?.afterVoeGuidelineApplications
      : null) ||
    [];
  applications.forEach((application) => {
    if (application.applicationId) {
      byId[application.applicationId] = application;
    }
    if (application.employmentId && application.incomeType) {
      byEmploymentAndType[
        `${application.employmentId}/${application.incomeType}`
      ] = application;
    }
  });

  return { byId, byEmploymentAndType };
}

function normalizeEvidence(evidence) {
  return (Array.isArray(evidence) ? evidence : []).map((item) => ({
    documentId: item.documentId || null,
    documentName: item.documentName || null,
    documentSlotId: item.documentSlotId || null,
    quote: item.quoteOrField || item.rawValue || null,
  }));
}

function normalizeComponent(raw, statusLabel, guidelineApplications) {
  if (!raw) {
    return null;
  }
  const employmentId =
    raw.employmentId || employmentIdFromFieldKey(raw.fieldKey);
  const application =
    guidelineApplications.byId[raw.guidelineApplicationId] ||
    guidelineApplications.byEmploymentAndType[
      `${employmentId}/${raw.incomeType}`
    ];
  const selectedRuleIds = Array.isArray(raw.selectedRuleIds)
    ? raw.selectedRuleIds
    : Array.isArray(raw.ruleIds)
      ? raw.ruleIds
      : [];

  return {
    incomeType: raw.incomeType,
    monthly: typeof raw.monthly === "number" ? raw.monthly : null,
    status: normalizeStatus(raw.status, statusLabel),
    formula: raw.formula || null,
    formulaAst: raw.formulaAst || application?.formulaAst || null,
    formulaInputs: extractFormulaInputs(raw.formula),
    reason: raw.reason || null,
    missingEvidence: Array.isArray(raw.missingEvidence)
      ? raw.missingEvidence
      : [],
    conditions: Array.isArray(raw.conditions)
      ? raw.conditions
      : application?.conditions || [],
    selectedRuleIds,
    rules: (application?.ruleApplications || [])
      .filter((rule) => rule.applies !== false)
      .map(normalizeRule),
    sourceFieldKeys: application?.includedFieldKeys || [],
    explanation: raw.explanation || null,
    evidence: normalizeEvidence(raw.evidence),
    employerName: raw.employerName || null,
    employmentId,
    borrowerId:
      raw.borrowerId ||
      (employmentId ? employmentId.split("-employment-")[0] : null),
    // Self-employment (Schedule C / S-Corp) components attach to a business
    // instead of an employment and carry their own per-tax-year analysis.
    businessId: raw.businessId || null,
    businessName: raw.businessName || null,
    calculationTrack: raw.calculationTrack || null,
    documentationMethod: raw.documentationMethod || null,
    stabilityMethod: raw.stabilityMethod || null,
    businessAgeStatus: raw.businessAgeStatus || null,
    businessAgeYears:
      typeof raw.businessAgeYears === "number" ? raw.businessAgeYears : null,
    ownershipPercent:
      typeof raw.ownershipPercent === "number" ? raw.ownershipPercent : null,
    ownershipPercentBasis: raw.ownershipPercentBasis || null,
    trend: raw.trend || null,
    annualAnalyses: Array.isArray(raw.annualAnalyses) ? raw.annualAnalyses : [],
  };
}

/**
 * Builds a map: employmentId -> { Base, Bonus, Overtime, Commission }
 * from the derived income breakdown. Qualifying components win over
 * conditional/blocked when the same type appears more than once.
 */
function indexComponentsByEmployment(
  derived,
  formulaRules,
  applicationsKey = "guidelineApplications",
) {
  const byEmployment = {};
  const guidelineApplications = indexGuidelineApplications(
    formulaRules,
    applicationsKey,
  );
  if (!derived) {
    return byEmployment;
  }

  const add = (list, statusLabel) => {
    (Array.isArray(list) ? list : []).forEach((raw) => {
      const comp = normalizeComponent(raw, statusLabel, guidelineApplications);
      if (!comp || !comp.employmentId || !comp.incomeType) {
        return;
      }
      if (!byEmployment[comp.employmentId]) {
        byEmployment[comp.employmentId] = {};
      }
      const bucket = byEmployment[comp.employmentId];
      bucket[comp.incomeType] = mergeComponent(bucket[comp.incomeType], comp);
    });
  };

  add(derived.components, STATUS_LABELS.qualifying);
  add(derived.conditionalComponents, STATUS_LABELS.conditional);
  add(derived.blockedComponents, STATUS_LABELS.excluded);

  return byEmployment;
}

/**
 * Self-employment components (Schedule C today, S-Corp next) are keyed by
 * businessId, not employmentId, so `indexComponentsByEmployment` drops them.
 * Builds businessId -> { [incomeType]: component }. The income types are taken
 * from whatever the pipeline emitted rather than a fixed wage list, so a new
 * business income type renders without another change here.
 */
function indexComponentsByBusiness(businessIncomes, derived) {
  const byBusiness = {};
  const lists = [
    ...businessIncomes.map((income) => income?.components),
    derived?.scheduleC?.components,
    derived?.sCorp?.components,
  ];

  lists.forEach((list) => {
    (Array.isArray(list) ? list : []).forEach((raw) => {
      const component = normalizeComponent(raw, STATUS_LABELS.conditional, {
        byId: {},
        byEmploymentAndType: {},
      });
      if (!component || !component.businessId || !component.incomeType) {
        return;
      }
      if (!byBusiness[component.businessId]) {
        byBusiness[component.businessId] = {};
      }
      const bucket = byBusiness[component.businessId];
      bucket[component.incomeType] = mergeComponent(
        bucket[component.incomeType],
        component,
      );
    });
  });

  return byBusiness;
}

/**
 * Splits the flat business field-decision array into per-business buckets. The
 * `business` scope drives the "Business details" panel; every other scope is a
 * value read off a source document and is grouped by document so the reviewer
 * can tie each figure back to a page.
 */
function indexBusinessFields(businessFieldDecisions) {
  const byBusiness = {};
  (Array.isArray(businessFieldDecisions) ? businessFieldDecisions : []).forEach(
    (field) => {
      const businessId = field?.businessId;
      if (!businessId) {
        return;
      }
      if (!byBusiness[businessId]) {
        byBusiness[businessId] = {
          businessName: null,
          borrowerId: null,
          decisionFields: [],
          documentsById: {},
        };
      }
      const bucket = byBusiness[businessId];
      bucket.businessName = bucket.businessName || field.businessName || null;
      bucket.borrowerId = bucket.borrowerId || field.borrowerId || null;

      if (field.scope === "business") {
        bucket.decisionFields.push(field);

        return;
      }

      const documentKey =
        field.documentId ||
        field.scopeIds?.documentId ||
        (field.taxYear ? `tax-year-${field.taxYear}` : "unsourced");
      if (!bucket.documentsById[documentKey]) {
        bucket.documentsById[documentKey] = {
          documentId: field.documentId || field.scopeIds?.documentId || null,
          documentName:
            field.documentName ||
            field.evidence?.[0]?.documentName ||
            field.sourceDocumentType ||
            null,
          taxYear: field.taxYear ?? field.scopeIds?.taxYear ?? null,
          fields: [],
        };
      }
      bucket.documentsById[documentKey].fields.push(field);
    },
  );

  return byBusiness;
}

function normalizeBusiness(businessId, income, fieldBucket) {
  const components = Object.values(income || {}).filter(Boolean);
  const totals = summarizeIncome(income);
  const structure = components.find((component) => component.calculationTrack)
    ?.calculationTrack;
  const documents = Object.values(fieldBucket?.documentsById || {})
    .map((document) => ({
      ...document,
      fields: normalizeFields(document.fields),
    }))
    .sort((a, b) => (b.taxYear || 0) - (a.taxYear || 0));

  return {
    businessId,
    businessName:
      fieldBucket?.businessName ||
      components.find((component) => component.businessName)?.businessName ||
      businessId,
    calculationTrack: structure || null,
    income,
    incomeTypes: components.map((component) => component.incomeType),
    totals,
    fields: normalizeFields(fieldBucket?.decisionFields),
    documents,
  };
}

/** Returns the business nodes belonging to one borrower. */
function businessesForBorrower(borrowerId, componentsByBusiness, fieldsByBusiness) {
  return Object.entries(componentsByBusiness)
    .filter(([businessId, income]) => {
      const owner =
        Object.values(income).find((component) => component?.borrowerId)
          ?.borrowerId || fieldsByBusiness[businessId]?.borrowerId;

      return owner === borrowerId;
    })
    .map(([businessId, income]) =>
      normalizeBusiness(businessId, income, fieldsByBusiness[businessId]),
    );
}

function normalizeField(field) {
  if (!field) {
    return null;
  }

  return {
    key: field.key || field.fieldId,
    fieldId: field.fieldId || field.key,
    label: field.label || field.fieldName || field.fieldId || field.key,
    value: field.finalValue,
    candidateValue: field.candidateValue,
    valueType: field.valueType || null,
    status: field.status || null,
    confidence: field.confidence || null,
    inclusionDecision: field.inclusionDecision || null,
    explanation: field.decisionExplanation || field.auditExplanation || null,
    ruleIds: Array.isArray(field.applicableGuidelineRuleIds)
      ? field.applicableGuidelineRuleIds
      : [],
  };
}

function normalizeFields(fields) {
  return (Array.isArray(fields) ? fields : [])
    .map(normalizeField)
    .filter(Boolean);
}

function valueFor(fields, fieldId) {
  return fields.find((field) => field.fieldId === fieldId)?.value ?? null;
}

function normalizeW2(w2, index) {
  const fields = normalizeFields(w2?.fields);
  const taxYear = valueFor(fields, "taxYear");

  return {
    slotId: w2?.slotId || `w2-${index + 1}`,
    documentId: w2?.documentId || null,
    documentName: w2?.documentName || null,
    status: w2?.status || "missing-evidence",
    taxYear,
    fields,
  };
}

function normalizeEarning(earning, index) {
  const fields = normalizeFields(earning?.fields);

  return {
    earningRowId: earning?.earningRowId || `earning-${index + 1}`,
    description: valueFor(fields, "description") || `Earning ${index + 1}`,
    incomeType: valueFor(fields, "incomeType"),
    currentPeriodAmount: valueFor(fields, "currentPeriodAmount"),
    yearToDateAmount: valueFor(fields, "yearToDateAmount"),
    fields,
  };
}

function normalizePaystub(paystub, index) {
  const fields = normalizeFields(paystub?.fields);

  return {
    slotId: paystub?.slotId || `paystub-${index + 1}`,
    documentId: paystub?.documentId || null,
    documentName: paystub?.documentName || null,
    status: paystub?.status || "missing-evidence",
    payDate: valueFor(fields, "payDate"),
    startDate: valueFor(fields, "startDate"),
    endDate: valueFor(fields, "endDate"),
    payFrequency: valueFor(fields, "payFrequency"),
    payType: valueFor(fields, "payType"),
    fields,
    earnings: (paystub?.earnings || []).map(normalizeEarning),
  };
}

function normalizeVoeEarning(earning, index) {
  const fields = normalizeFields(earning?.fields);

  return {
    earningRowId: earning?.earningRowId || `voe-earning-${index + 1}`,
    description:
      valueFor(fields, "voeDescription") || `VOE earning ${index + 1}`,
    incomeType: valueFor(fields, "voeIncomeType"),
    periodType: valueFor(fields, "voePeriodType"),
    periodYear: valueFor(fields, "voePeriodYear"),
    amount: valueFor(fields, "voeAmount"),
    fields,
  };
}

function normalizeVoe(voe, index) {
  const fields = normalizeFields(voe?.fields);

  return {
    slotId: voe?.slotId || `voe-${index + 1}`,
    documentId: voe?.documentId || null,
    documentName: voe?.documentName || null,
    status: voe?.status || "missing-evidence",
    statementDate: valueFor(fields, "voeStatementDate"),
    payFrequency: valueFor(fields, "voePayFrequency"),
    payType: valueFor(fields, "voePayType"),
    annualSalary: valueFor(fields, "voeAnnualSalary"),
    baseHourlyRate: valueFor(fields, "voeBaseHourlyRate"),
    hoursPerWeek: valueFor(fields, "voeHoursPerWeek"),
    fields,
    earnings: (voe?.earnings || []).map(normalizeVoeEarning),
  };
}

function normalizeDocuments(employment, voeEmployment) {
  const voeDocuments =
    voeEmployment?.voes?.length > 0 ? voeEmployment.voes : employment?.voes;

  return {
    w2s: (employment?.w2s || []).map(normalizeW2),
    paystubs: (employment?.paystubs || []).map(normalizePaystub),
    voes: (voeDocuments || []).map(normalizeVoe),
  };
}

function indexVoeEmployments(voeFieldDecisions) {
  const byEmployment = {};
  const borrowers = Array.isArray(voeFieldDecisions?.borrowers)
    ? voeFieldDecisions.borrowers
    : [];
  const topLevelEmployments = Array.isArray(voeFieldDecisions?.employments)
    ? voeFieldDecisions.employments
    : [];

  borrowers.forEach((borrower) => {
    (borrower.employments || []).forEach((employment) => {
      if (employment.employmentId) {
        byEmployment[employment.employmentId] = employment;
      }
    });
  });
  topLevelEmployments.forEach((employment) => {
    if (employment.employmentId) {
      byEmployment[employment.employmentId] = employment;
    }
  });

  return byEmployment;
}

/**
 * Pulls the labelled values out of the planner's human-readable formula.
 * This lets the UI expose each substituted input beside the original formula.
 */
export function extractFormulaInputs(formula) {
  if (typeof formula !== "string") {
    return [];
  }
  const matches = [];
  const pattern = /([A-Za-z][A-Za-z0-9 -]*?)\s*\((-?\d+(?:\.\d+)?)\)/g;
  let match = pattern.exec(formula);
  while (match) {
    matches.push({
      label: match[1].trim(),
      value: Number(match[2]),
    });
    match = pattern.exec(formula);
  }

  return matches;
}

/** Fallback: derive income rows from the hierarchical computedIncome entries. */
function computedIncomeToRows(computedIncome) {
  const income = {};
  (Array.isArray(computedIncome) ? computedIncome : []).forEach((entry) => {
    const type =
      entry.incomeType ||
      (entry.fieldId ? entry.fieldId.replace(/Monthly$/i, "") : null);
    if (!type) {
      return;
    }
    const normalizedType =
      type.charAt(0).toUpperCase() + type.slice(1).replace("ime", "ime");
    income[normalizedType] = {
      incomeType: normalizedType,
      monthly: typeof entry.finalValue === "number" ? entry.finalValue : null,
      status:
        entry.qualificationStatus === "conditional" ||
        entry.status === "manual-review" ||
        entry.inclusionDecision === "insufficient-evidence"
          ? STATUS_LABELS.conditional
          : STATUS_LABELS.qualifying,
      formula: entry.formula || null,
      formulaInputs: extractFormulaInputs(entry.formula),
      reason: null,
      missingEvidence: [],
      conditions: [],
      selectedRuleIds: Array.isArray(entry.applicableGuidelineRuleIds)
        ? entry.applicableGuidelineRuleIds
        : [],
      rules: [],
      explanation: entry.decisionExplanation || null,
      evidence: normalizeEvidence(entry.evidence),
    };
  });

  return income;
}

function indexReconciliationComponents(reconciliation) {
  return Object.fromEntries(
    (reconciliation?.components || [])
      .filter((component) => component.employmentId && component.incomeType)
      .map((component) => [
        `${component.employmentId}/${component.incomeType}`,
        component,
      ]),
  );
}

function indexReconciliationDecisions(reconciliation) {
  return Object.fromEntries(
    (reconciliation?.decisions || [])
      .filter((decision) => decision.employmentId)
      .map((decision) => [decision.employmentId, decision]),
  );
}

function timelineForIncomeType(timeline, incomeType) {
  return (Array.isArray(timeline) ? timeline : []).filter(
    (row) => !row.incomeType || row.incomeType === incomeType,
  );
}

function mergeAuditComponent(raw, normalized) {
  if (!raw) {
    return normalized || null;
  }
  const formula = raw.formula || normalized?.formula || null;
  const rawRuleIds = Array.isArray(raw.selectedRuleIds)
    ? raw.selectedRuleIds
    : Array.isArray(raw.ruleIds)
      ? raw.ruleIds
      : [];

  return {
    ...normalized,
    ...raw,
    formula,
    formulaInputs: extractFormulaInputs(formula),
    selectedRuleIds: Array.from(
      new Set([...(normalized?.selectedRuleIds || []), ...rawRuleIds]),
    ),
    rules: normalized?.rules || [],
  };
}

/**
 * Main entry: returns { borrowers: [...] } where each borrower has
 * employments, each employment has an `income` map keyed by income type,
 * and each borrower carries its available/qualifying totals.
 */
export function buildIncomeTree(artifacts) {
  if (!artifacts) {
    return { borrowers: [] };
  }
  const {
    result,
    derived,
    fieldDecisions,
    formulaRules,
    voeFieldDecisions,
    scheduleCIncome,
    scheduleCFieldDecisions,
    sCorpIncome,
    sCorpFieldDecisions,
  } = artifacts;
  const reconciliation =
    artifacts.voeReconciliation || derived?.reconciliation || null;
  const hasVoeReconciliation = Boolean(reconciliation);
  const hasIndependentVoeReconciliation = Boolean(
    reconciliation?.voeOnly ||
    reconciliation?.components?.some(
      (component) => component.voeOnly || component.selectedMethod,
    ),
  );
  const baselineDerived = derived?.beforeVoe || derived;
  const voeOnlyDerived =
    derived?.voeOnly || reconciliation?.voeOnlyDerived || null;
  const componentsByEmployment = indexComponentsByEmployment(
    derived,
    formulaRules,
    derived?.beforeVoe
      ? "reconciliationGuidelineApplications"
      : "guidelineApplications",
  );
  const baselineComponentsByEmployment = indexComponentsByEmployment(
    baselineDerived,
    formulaRules,
    "guidelineApplications",
  );
  const voeOnlyComponentsByEmployment = indexComponentsByEmployment(
    voeOnlyDerived,
    formulaRules,
    "voeOnlyGuidelineApplications",
  );
  const reconciliationByComponent =
    indexReconciliationComponents(reconciliation);
  const reconciliationDecisions = indexReconciliationDecisions(reconciliation);
  const voeEmployments = indexVoeEmployments(voeFieldDecisions);
  const componentsByBusiness = indexComponentsByBusiness(
    [scheduleCIncome, sCorpIncome],
    derived,
  );
  const businessFields = indexBusinessFields([
    ...(Array.isArray(scheduleCFieldDecisions) ? scheduleCFieldDecisions : []),
    ...(Array.isArray(sCorpFieldDecisions) ? sCorpFieldDecisions : []),
  ]);

  const availableByBorrower =
    (result && result.availableMonthlyIncomeByBorrower) ||
    (derived && derived.availableMonthlyIncomeByBorrower) ||
    {};
  const qualifyingByBorrower =
    (result && result.qualifyingMonthlyIncomeByBorrower) ||
    (derived && derived.qualifyingMonthlyIncomeByBorrower) ||
    {};
  const baselineAvailableByBorrower =
    result?.baselineAvailableMonthlyIncomeByBorrower ||
    baselineDerived?.availableMonthlyIncomeByBorrower ||
    availableByBorrower;
  const baselineQualifyingByBorrower =
    result?.baselineQualifyingMonthlyIncomeByBorrower ||
    baselineDerived?.qualifyingMonthlyIncomeByBorrower ||
    qualifyingByBorrower;
  const voeOnlyAvailableByBorrower =
    reconciliation?.voeOnly?.availableMonthlyIncomeByBorrower ||
    voeOnlyDerived?.availableMonthlyIncomeByBorrower ||
    {};
  const voeOnlyQualifyingByBorrower =
    reconciliation?.voeOnly?.qualifyingMonthlyIncomeByBorrower ||
    voeOnlyDerived?.qualifyingMonthlyIncomeByBorrower ||
    {};
  const finalAfterVoeAvailable = numOrNull(
    result?.finalAfterVoeAvailableMonthlyIncomeTotal ??
      reconciliation?.final?.availableMonthlyIncomeTotal ??
      (hasVoeReconciliation
        ? (result?.availableMonthlyIncomeTotal ??
          derived?.availableMonthlyIncomeTotal)
        : null),
  );
  const finalAfterVoeQualifying = numOrNull(
    result?.finalAfterVoeQualifyingMonthlyIncomeTotal ??
      reconciliation?.final?.qualifyingMonthlyIncomeTotal ??
      (hasVoeReconciliation
        ? (result?.qualifyingMonthlyIncomeTotal ??
          derived?.qualifyingMonthlyIncomeTotal)
        : null),
  );

  // Preferred source: the hierarchical field-decision tree (has names + fields).
  if (fieldDecisions && Array.isArray(fieldDecisions.borrowers)) {
    const borrowers = fieldDecisions.borrowers.map((b) => ({
      borrowerId: b.borrowerId,
      borrowerName: b.borrowerName,
      availableTotal: numOrNull(availableByBorrower[b.borrowerId]),
      qualifyingTotal: numOrNull(qualifyingByBorrower[b.borrowerId]),
      baselineAvailableTotal: numOrNull(
        baselineAvailableByBorrower[b.borrowerId],
      ),
      baselineQualifyingTotal: numOrNull(
        baselineQualifyingByBorrower[b.borrowerId],
      ),
      voeOnlyAvailableTotal: numOrNull(
        voeOnlyAvailableByBorrower[b.borrowerId],
      ),
      voeOnlyQualifyingTotal: numOrNull(
        voeOnlyQualifyingByBorrower[b.borrowerId],
      ),
      employments: (b.employments || []).map((emp) =>
        normalizeEmployment(
          emp,
          componentsByEmployment[emp.employmentId],
          baselineComponentsByEmployment[emp.employmentId],
          voeOnlyComponentsByEmployment[emp.employmentId],
          reconciliationByComponent,
          reconciliationDecisions[emp.employmentId],
          voeEmployments[emp.employmentId],
        ),
      ),
      businesses: businessesForBorrower(
        b.borrowerId,
        componentsByBusiness,
        businessFields,
      ),
    }));

    return {
      borrowers,
      loanTotals: {
        available: numOrNull(
          result?.availableMonthlyIncomeTotal ??
            derived?.availableMonthlyIncomeTotal,
        ),
        qualifying: numOrNull(
          result?.qualifyingMonthlyIncomeTotal ??
            derived?.qualifyingMonthlyIncomeTotal,
        ),
        baselineAvailable: numOrNull(
          result?.baselineAvailableMonthlyIncomeTotal ??
            baselineDerived?.availableMonthlyIncomeTotal,
        ),
        baselineQualifying: numOrNull(
          result?.baselineQualifyingMonthlyIncomeTotal ??
            baselineDerived?.qualifyingMonthlyIncomeTotal,
        ),
        voeOnlyAvailable: numOrNull(
          reconciliation?.voeOnly?.availableMonthlyIncomeTotal ??
            voeOnlyDerived?.availableMonthlyIncomeTotal,
        ),
        voeOnlyQualifying: numOrNull(
          reconciliation?.voeOnly?.qualifyingMonthlyIncomeTotal ??
            voeOnlyDerived?.qualifyingMonthlyIncomeTotal,
        ),
        finalAfterVoeAvailable,
        finalAfterVoeQualifying,
        difference: differenceOrNull(
          result?.baselineQualifyingMonthlyIncomeTotal ??
            baselineDerived?.qualifyingMonthlyIncomeTotal,
          finalAfterVoeQualifying,
        ),
      },
      hasVoeReconciliation,
      hasIndependentVoeReconciliation,
      reconciliationStatus: reconciliation?.status || null,
    };
  }

  // Fallback: reconstruct the tree from the derived components alone.
  return {
    borrowers: borrowersFromDerived(
      componentsByEmployment,
      baselineComponentsByEmployment,
      {
        availableByBorrower,
        qualifyingByBorrower,
        baselineAvailableByBorrower,
        baselineQualifyingByBorrower,
        voeOnlyAvailableByBorrower,
        voeOnlyQualifyingByBorrower,
      },
      voeOnlyComponentsByEmployment,
      reconciliationByComponent,
      reconciliationDecisions,
      componentsByBusiness,
      businessFields,
    ),
    loanTotals: {
      available: numOrNull(
        result?.availableMonthlyIncomeTotal ??
          derived?.availableMonthlyIncomeTotal,
      ),
      qualifying: numOrNull(
        result?.qualifyingMonthlyIncomeTotal ??
          derived?.qualifyingMonthlyIncomeTotal,
      ),
      baselineAvailable: numOrNull(
        result?.baselineAvailableMonthlyIncomeTotal ??
          baselineDerived?.availableMonthlyIncomeTotal,
      ),
      baselineQualifying: numOrNull(
        result?.baselineQualifyingMonthlyIncomeTotal ??
          baselineDerived?.qualifyingMonthlyIncomeTotal,
      ),
      voeOnlyAvailable: numOrNull(
        reconciliation?.voeOnly?.availableMonthlyIncomeTotal ??
          voeOnlyDerived?.availableMonthlyIncomeTotal,
      ),
      voeOnlyQualifying: numOrNull(
        reconciliation?.voeOnly?.qualifyingMonthlyIncomeTotal ??
          voeOnlyDerived?.qualifyingMonthlyIncomeTotal,
      ),
      finalAfterVoeAvailable,
      finalAfterVoeQualifying,
      difference: differenceOrNull(
        result?.baselineQualifyingMonthlyIncomeTotal ??
          baselineDerived?.qualifyingMonthlyIncomeTotal,
        finalAfterVoeQualifying,
      ),
    },
    hasVoeReconciliation,
    hasIndependentVoeReconciliation,
    reconciliationStatus: reconciliation?.status || null,
  };
}

function mergeComponent(primary, secondary) {
  if (!primary) {
    return secondary || null;
  }
  if (!secondary) {
    return primary;
  }

  return {
    ...secondary,
    ...primary,
    formula: primary.formula || secondary.formula,
    formulaAst: primary.formulaAst || secondary.formulaAst,
    formulaInputs: primary.formulaInputs?.length
      ? primary.formulaInputs
      : secondary.formulaInputs || [],
    selectedRuleIds: Array.from(
      new Set([
        ...(primary.selectedRuleIds || []),
        ...(secondary.selectedRuleIds || []),
      ]),
    ),
    rules: primary.rules?.length ? primary.rules : secondary.rules || [],
    explanation: primary.explanation || secondary.explanation || null,
    evidence: primary.evidence?.length
      ? primary.evidence
      : secondary.evidence || [],
  };
}

function summarizeIncome(income) {
  const components = Object.values(income || {}).filter(
    (component) => component && typeof component.monthly === "number",
  );
  const availableTerms = components.filter(
    (component) => component.status !== STATUS_LABELS.excluded,
  );
  const qualifyingTerms = components.filter(
    (component) => component.status === STATUS_LABELS.qualifying,
  );

  return {
    availableTerms,
    qualifyingTerms,
    availableTotal: availableTerms.reduce(
      (sum, component) => sum + component.monthly,
      0,
    ),
    qualifyingTotal: qualifyingTerms.reduce(
      (sum, component) => sum + component.monthly,
      0,
    ),
  };
}

function normalizeEmployment(
  employment,
  derivedIncome,
  baselineDerivedIncome,
  voeOnlyDerivedIncome,
  reconciliationByComponent,
  reconciliationDecision,
  voeEmployment,
) {
  const finalIncome = mergeIncome(
    derivedIncome,
    computedIncomeToRows(
      employment.reconciledIncome?.length
        ? employment.reconciledIncome
        : employment.computedAfterVoeIncome?.length
          ? employment.computedAfterVoeIncome
          : employment.computedIncome,
    ),
  );
  const baselineIncome = mergeIncome(
    baselineDerivedIncome,
    computedIncomeToRows(employment.computedIncome),
  );
  const voeOnlyIncome = mergeIncome(
    voeOnlyDerivedIncome,
    computedIncomeToRows(employment.computedAfterVoeIncome),
  );
  const income = {};
  INCOME_TYPES.forEach((incomeType) => {
    const comparison =
      reconciliationByComponent?.[`${employment.employmentId}/${incomeType}`] ||
      null;
    const finalComponent = mergeAuditComponent(
      comparison?.final || comparison?.afterVoe,
      finalIncome[incomeType],
    );
    const baselineComponent = mergeAuditComponent(
      comparison?.baseline,
      baselineIncome[incomeType],
    );
    const voeOnlyComponent = mergeAuditComponent(
      comparison?.voeOnly || comparison?.afterVoe,
      voeOnlyIncome[incomeType],
    );
    const componentDecision =
      reconciliationDecision?.componentDecisions?.find(
        (decision) => decision.incomeType === incomeType,
      ) || null;
    income[incomeType] = finalComponent
      ? {
          ...finalComponent,
          baseline: baselineComponent,
          voeOnly: voeOnlyComponent,
          final: finalComponent,
          beforeVoe: baselineComponent,
          afterVoe: voeOnlyComponent || finalComponent,
          difference:
            comparison?.difference ??
            differenceOrNull(
              baselineComponent?.monthly,
              finalComponent.monthly,
            ),
          finalSource: comparison?.finalSource || null,
          selectedMethod:
            comparison?.selectedMethod ||
            componentDecision?.selectedMethod ||
            comparison?.finalSource ||
            null,
          rejectedAlternative:
            comparison?.rejectedAlternative ||
            componentDecision?.rejectedAlternative ||
            null,
          hasVoeComparison: Boolean(comparison),
          hasIndependentVoeBranch: Boolean(
            comparison?.voeOnly || comparison?.selectedMethod,
          ),
          voeFieldsUsed: comparison?.voeFieldsUsed || [],
          changedFields: comparison?.changedFields || [],
          resolvedConditions: comparison?.resolvedConditions || [],
          remainingConditions: comparison?.remainingConditions || [],
          reconciliationExplanation: comparison?.explanation || null,
          evidenceTimeline: timelineForIncomeType(
            reconciliationDecision?.timeline,
            incomeType,
          ),
        }
      : null;
  });
  const totals = summarizeIncome(income);
  const baselineTotals = summarizeIncome(baselineIncome);
  const voeOnlyTotals = summarizeIncome(voeOnlyIncome);

  return {
    employmentId: employment.employmentId,
    employerName: employment.employerName,
    status: employment.employmentStatus || employment.status || null,
    income,
    totals: {
      ...totals,
      baselineAvailableTotal: baselineTotals.availableTotal,
      baselineQualifyingTotal: baselineTotals.qualifyingTotal,
      voeOnlyAvailableTotal: voeOnlyTotals.availableTotal,
      voeOnlyQualifyingTotal: voeOnlyTotals.qualifyingTotal,
      availableDifference:
        totals.availableTotal - baselineTotals.availableTotal,
      qualifyingDifference:
        totals.qualifyingTotal - baselineTotals.qualifyingTotal,
    },
    fields: normalizeFields(employment.fields),
    documents: normalizeDocuments(employment, voeEmployment),
  };
}

/** Prefer derived components, then enrich them with computed-income audit data. */
function mergeIncome(fromDerived, fromComputed) {
  const income = {};
  INCOME_TYPES.forEach((type) => {
    income[type] = mergeComponent(
      fromDerived && fromDerived[type],
      fromComputed && fromComputed[type],
    );
  });

  return income;
}

function borrowersFromDerived(
  componentsByEmployment,
  baselineComponentsByEmployment,
  totals,
  voeOnlyComponentsByEmployment,
  reconciliationByComponent,
  reconciliationDecisions,
  componentsByBusiness = {},
  businessFields = {},
) {
  const byBorrower = {};
  const ensureBorrower = (borrowerId) => {
    if (!byBorrower[borrowerId]) {
      byBorrower[borrowerId] = {
        borrowerId,
        borrowerName: borrowerId,
        availableTotal: numOrNull(totals.availableByBorrower[borrowerId]),
        qualifyingTotal: numOrNull(totals.qualifyingByBorrower[borrowerId]),
        baselineAvailableTotal: numOrNull(
          totals.baselineAvailableByBorrower[borrowerId],
        ),
        baselineQualifyingTotal: numOrNull(
          totals.baselineQualifyingByBorrower[borrowerId],
        ),
        voeOnlyAvailableTotal: numOrNull(
          totals.voeOnlyAvailableByBorrower[borrowerId],
        ),
        voeOnlyQualifyingTotal: numOrNull(
          totals.voeOnlyQualifyingByBorrower[borrowerId],
        ),
        employments: [],
        businesses: [],
      };
    }

    return byBorrower[borrowerId];
  };

  Object.entries(componentsByEmployment).forEach(([employmentId, income]) => {
    const borrowerId = employmentId.split("-employment-")[0];
    const employerName = Object.values(income).find(
      (c) => c && c.employerName,
    )?.employerName;
    ensureBorrower(borrowerId).employments.push(
      normalizeEmployment(
        {
          employmentId,
          employerName: employerName || employmentId,
          fields: [],
        },
        income,
        baselineComponentsByEmployment[employmentId],
        voeOnlyComponentsByEmployment[employmentId],
        reconciliationByComponent,
        reconciliationDecisions[employmentId],
        null,
      ),
    );
  });

  // A self-employed borrower can have no employment at all, so businesses must
  // be able to create the borrower row rather than only decorate an existing one.
  Object.entries(componentsByBusiness).forEach(([businessId, income]) => {
    const borrowerId =
      Object.values(income).find((component) => component?.borrowerId)
        ?.borrowerId || businessFields[businessId]?.borrowerId;
    if (!borrowerId) {
      return;
    }
    ensureBorrower(borrowerId).businesses.push(
      normalizeBusiness(businessId, income, businessFields[businessId]),
    );
  });

  return Object.values(byBorrower);
}

function numOrNull(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function differenceOrNull(before, after) {
  const normalizedBefore = numOrNull(before);
  const normalizedAfter = numOrNull(after);
  if (normalizedBefore === null || normalizedAfter === null) {
    return null;
  }

  return Math.round((normalizedAfter - normalizedBefore) * 100) / 100;
}

/** Human-readable "how we got the totals" strings from the artifacts. */
export function pickFinalFormula(artifacts) {
  const result = artifacts?.result || {};
  const derived = artifacts?.derived || {};

  return {
    qualifying: result.finalFormula || derived.finalFormula || null,
    available: derived.availableFormula || null,
    expanded: derived.expandedFormula || null,
  };
}
